"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import Form from "next/form";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMediaQuery } from "usehooks-ts";

import { css } from "@/../styled-system/css";
import { CookiesData, createCookies } from "@/actions/cookies/create-cookies";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/Drawer";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  ReactForm,
} from "@/components/ui/ReactForm";
import {
  TCookiesConsentFormSchema,
  cookiesConsentFormSchema,
} from "@/types/CookiesConsentFormTypes/types";

import VerticalScrollArea from "./VerticalScrollArea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/Accordion";
import Button from "../ui/Button";
import { Switch } from "../ui/Switch";

function CookiesDialog() {
  const [open, setOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 62rem)");

  const handleAllowAll = async () => {
    setIsSubmitting(true);
    try {
      const data: CookiesData = {
        necessaryCookies: true,
        preferencesCookies: true,
        statisticsCookies: true,
        marketingCookies: true,
      };
      await createCookies(data);
      setOpen(false);
    } catch (error) {
      console.error("Error setting cookies:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeny = () => {
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    // Prevent closing unless specifically allowed
    if (!isOpen) return;
    setOpen(isOpen);
  };

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          css={{ maxWidth: "700px", maxHeight: "90vh", p: "4", gap: "0" }}
        >
          <div className={css({ px: "4", pt: "4", pb: "2" })}>
            <DialogTitle>Cookie Settings</DialogTitle>
            <DialogDescription>
              Solecit uses cookies in order to personalize content for users,
              model training, and traffic analysis.
            </DialogDescription>
          </div>
          <CookieForm setOpen={setOpen} setIsSubmitting={setIsSubmitting} />
          <DialogFooter css={{ gap: "3", px: "4", pb: "4", pt: "2" }}>
            <Button
              variant="outline"
              width="fill"
              css={{ flexShrink: "1" }}
              onClick={handleDeny}
              disabled={isSubmitting}
            >
              <span>Deny</span>
            </Button>
            <Button
              type="submit"
              form="cookiesConsentForm"
              variant="black"
              width="fill"
              css={{ flexShrink: "1" }}
              disabled={isSubmitting}
              onClick={handleAllowAll}
            >
              <span className={css({ color: "white" })}>Allow</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} dismissible={false}>
      <DrawerContent>
        <div
          className={css({
            maxWidth: "700px",
            display: "flex",
            flexDirection: "column",
            maxHeight: "95dvh",
            width: "95%",
            mx: "auto",
            pt: "4",
          })}
        >
          <DrawerHeader containHandle={false} css={{ pb: "2" }}>
            <DrawerTitle>Cookie Settings</DrawerTitle>
            <DrawerDescription>
              Solecit uses cookies in order to personalize content for users,
              model training, and traffic analysis.
            </DrawerDescription>
          </DrawerHeader>
          <CookieForm setOpen={setOpen} setIsSubmitting={setIsSubmitting} />
          <DrawerFooter css={{ pt: "2" }}>
            <Button
              type="submit"
              form="cookiesConsentForm"
              variant="black"
              width="fill"
              disabled={isSubmitting}
              onClick={handleAllowAll}
            >
              <span className={css({ color: "white" })}>Allow All</span>
            </Button>
            <Button
              variant="outline"
              width="fill"
              onClick={handleDeny}
              disabled={isSubmitting}
            >
              <span>Deny</span>
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

interface CookieFormProps {
  setIsSubmitting: (value: boolean) => void;
  setOpen: (value: boolean) => void;
}

const accordionTriggerCSS = {
  flexDirection: "row-reverse",
  justifyContent: "start",
  color: "gray.500",
  _hover: {
    boxShadow: "unset",
    color: "neutral.700",
  },
  _expanded: {
    color: "neutral.700",
    boxShadow: "unset",
  },
};

function CookieForm({ setIsSubmitting, setOpen }: CookieFormProps) {
  const onSubmitForm = async (data: TCookiesConsentFormSchema) => {
    setIsSubmitting(true);
    try {
      const response = await fetch("/api/closet/custom-upload", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const responseData = await response.json();

      if (!responseData.ok) {
        setOpen(false);
      } else {
        setOpen(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const form = useForm<TCookiesConsentFormSchema>({
    resolver: zodResolver(cookiesConsentFormSchema),
    defaultValues: {
      necessaryCookies: true,
      preferencesCookies: true,
      statisticsCookies: true,
      marketingCookies: true,
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  return (
    <VerticalScrollArea type="auto">
      <div
        className={css({
          maxHeight: "100%",
          px: "4",
          py: "2",
        })}
      >
        <ReactForm {...form}>
          <Form
            id="cookiesConsentForm"
            onSubmit={handleSubmit(onSubmitForm)}
            action="/cookies"
          >
            <Accordion type="multiple">
              <AccordionItem value="accordion-neccessary-cookies">
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "space-between",
                  })}
                >
                  <AccordionTrigger css={accordionTriggerCSS}>
                    Necessary
                  </AccordionTrigger>
                  <FormField
                    control={control}
                    name="necessaryCookies"
                    render={({ field }) => (
                      <FormItem
                        css={{
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12",
                        }}
                      >
                        <FormLabel css={{ srOnly: "true" }}>
                          Necessary
                        </FormLabel>
                        <FormControl>
                          <Switch
                            id="necessary-cookies"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={true}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <AccordionContent>
                  <p>
                    Cookies that help make a website usable by enabling basic
                    functions like page navigation and access to secure areas of
                    the website. The website cannot fucntion properly without
                    these cookies.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="accordion-preferences-cookies">
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "space-between",
                  })}
                >
                  <AccordionTrigger css={accordionTriggerCSS}>
                    Preferences
                  </AccordionTrigger>
                  <FormField
                    control={control}
                    name="preferencesCookies"
                    render={({ field }) => (
                      <FormItem
                        css={{
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12",
                        }}
                      >
                        <FormLabel css={{ srOnly: "true" }}>
                          Preferences
                        </FormLabel>
                        <FormControl>
                          <Switch
                            id="preferences-cookies"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <AccordionContent>
                  <p>
                    Cookies that enable a wesite to remember information that
                    changes the way the website behaves or looks, like your
                    preferred language or the region that you are in.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="accordion-statistics-cookies">
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "space-between",
                  })}
                >
                  <AccordionTrigger css={accordionTriggerCSS}>
                    Statistics
                  </AccordionTrigger>
                  <FormField
                    control={control}
                    name="statisticsCookies"
                    render={({ field }) => (
                      <FormItem
                        css={{
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12",
                        }}
                      >
                        <FormLabel css={{ srOnly: "true" }}>
                          Statistics
                        </FormLabel>
                        <FormControl>
                          <Switch
                            id="statistics-cookies"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <AccordionContent>
                  <p>
                    Cookies that help website owners to understand how visitors
                    interact with websites by collecting and reporting
                    information anonymously.
                  </p>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem
                css={{
                  border: "none",
                }}
                value="accordion-marketing-cookies"
              >
                <div
                  className={css({
                    display: "flex",
                    justifyContent: "space-between",
                  })}
                >
                  <AccordionTrigger css={accordionTriggerCSS}>
                    Marketing
                  </AccordionTrigger>
                  <FormField
                    control={control}
                    name="marketingCookies"
                    render={({ field }) => (
                      <FormItem
                        css={{
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "12",
                        }}
                      >
                        <FormLabel css={{ srOnly: "true" }}>
                          Marketing
                        </FormLabel>
                        <FormControl>
                          <Switch
                            id="marketing-cookies"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <AccordionContent>
                  Cookies that are used to track visitors across websites. The
                  intention is to display ads that are relevant and engaging for
                  the individual user and thereby more valuable for publishers
                  and third party advertisers.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Form>
        </ReactForm>
      </div>
    </VerticalScrollArea>
  );
}

export default CookiesDialog;
